import { LoadingOutlined } from "@ant-design/icons";
import { useModal } from "@ebay/nice-modal-react";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Spin,
  message,
} from "antd";
import AddressModal from "components/foundation/AdddresModal/AddressModalV1";
import BaseModal from "components/foundation/modal/BaseModal";
import ModalItem from "components/foundation/modal/ModalItem";
import dayjs from "utils/config/dayjs.config";
import customParseFormat from "dayjs/plugin/customParseFormat";
import React, { useEffect } from "react";
import { GENDER, TYPEGTTT } from "utils/Constants/Constants";
import DKTTBDetailModel from "../../viewmodel/model/dkttb/DKTTBDetailModel";
import ImgDetail from "../modal/ImgDetail";

dayjs.extend(customParseFormat);

const antIcon = (
  <LoadingOutlined
    style={{
      fontSize: 42,
      color: "var(--mk-p-color)",
    }}
    spin
  />
);

const DKTTBDetail: React.FC = () => {
  const [form] = Form.useForm();
  const userModal = useModal(BaseModal);

  const {
    loading,
    detailDK,
    resultObject,
    showModal,
    wards,
    districts,
    provinces,
    provinceCode,
    wardCode,
    permanentProvinceCode,
    permanentDistrictCode,
    permanentWardCode,
    districtCode,
    setDistrictCode,
    setProvinceCode,
    setWardCode,
    setPermanentProvinceCode,
    setPermanentDistrictCode,
    setPermanentWardCode,
    setWards,
    setDistricts,
    handleClose,
    handleOpen,
    getListHistory,
    reload,
    setResultObject,
    onConfirm,
    handleNav,
  } = DKTTBDetailModel();

  const imgSimValue = Form.useWatch("sim_base_image", form);
  const imgCccdFrontValue = Form.useWatch("id_front_image", form);
  const imgCccdBackValue = Form.useWatch("id_back_image", form);
  const imgPortraitValue = Form.useWatch("portrait_image", form);
  const imgSignatureValue = Form.useWatch("signature_image", form);

  const fieldsToSet = [
    "id_type",
    "id_number",
    "name",
    "dob",
    "gender",
    "id_expired_date",
    "id_issued_date",
    "contact_address",
    "permanent_address",
    "place_address",
    "permanent_district",
    "permanent_province",
    "permanent_ward",
    "ward",
    "district",
    "province",
    "sim_base_image",
    "id_front_image",
    "id_back_image",
    "portrait_image",
    "signature_image",
    "contact_image_url",
    "id_issued_place",
  ];

  const handleImg = (info: any, field: any) => {
    form.setFieldsValue({ [field]: info });
  };

  const onClose = async () => {
    userModal.hide();
  };

  const detail = (info: any, field: any, type?: any) => {
    const hasImage = !!info;

    userModal
      .show({
        title: hasImage ? "Xem chi tiết" : "Tải ảnh lên",
        children: (
          <ImgDetail
            img={info}
            field={field}
            onClick={handleImg}
            onClose={onClose}
            type={type}
          />
        ),
        className: "modal-40",
        footer: hasImage
          ? [
              {
                title: "Đóng",
                htmlType: "button",
                type: "primary",
              },
            ]
          : [],
      })
      .then((values: any) => {})
      .catch((e) => {});
  };

  const handleConfirm = () => {
    const info = form.getFieldsValue();
    const dateFields = ["dob", "id_issued_date", "id_expired_date"];
    dateFields.forEach((field) => {
      const value = info[field];
      if (value && dayjs.isDayjs(value)) {
        info[field] = value.format("DD/MM/YYYY");
      }
    });

    const objectFields = ["id_type", "gender"];
    objectFields.forEach((field) => {
      const value = info[field];
      if (value && typeof value === "object" && "value" in value) {
        info[field] = value.value;
      }
    });

    const values = {
      ...info,
      contact_image_url: detailDK?.contact_image_url || "",
      province_code: +provinceCode,
      district_code: +districtCode,
      ward_code: +wardCode,
      permanent_province_code: +permanentProvinceCode,
      permanent_district_code: +permanentDistrictCode,
      permanent_ward_code: +permanentWardCode,
    };

    onConfirm(values);
  };

  useEffect(() => {
    if (!detailDK) return;

    if (!detailDK.id_number) {
      setResultObject({
        type: "error",
        message: "Không có thông tin đăng ký, Vui lòng nhập tay",
      });
      return;
    }

    const formData: { [key: string]: any } = {};

    fieldsToSet.forEach((field) => {
      const value = detailDK[field];

      if (field === "id_expired_date") {
        if (value === "Không thời hạn") {
          formData[field] = value;
        } else if (typeof value === "string") {
          const date = dayjs(
            value,
            ["DD/MM/YYYY", "DD-MM-YYYY", "DDMMYYYY"],
            true,
          );
          formData[field] = date.isValid() ? date : null;
        }
      } else if (
        ["id_issued_date", "dob"].includes(field) &&
        typeof value === "string"
      ) {
        const date = dayjs(
          value,
          ["DD/MM/YYYY", "DD-MM-YYYY", "DDMMYYYY"],
          true,
        );
        formData[field] = date.isValid() ? date : null;
      } else if (field === "gender") {
        const genderItem = GENDER.find((item) => item.value === value);
        formData[field] = genderItem?.value || null;
      } else if (field === "id_type") {
        const type = TYPEGTTT.find((item) => item.value === value);
        formData[field] = type?.value || null;
      } else {
        formData[field] = value;
      }
    });

    setProvinceCode(detailDK?.province_code);
    setDistrictCode(detailDK?.district_code);
    setWardCode(detailDK?.ward_code);
    setPermanentProvinceCode(detailDK?.permanent_province_code);
    setPermanentDistrictCode(detailDK?.permanent_district_code);
    setPermanentWardCode(detailDK?.permanent_ward_code);

    form.setFieldsValue(formData);
  }, [detailDK]);

  useEffect(() => {
    if (!!resultObject?.message) {
      switch (resultObject?.type) {
        case "success":
          message.success(resultObject?.message);
          break;
        case "error":
          message.error(resultObject?.message);
          break;
        default:
          break;
      }
    }
  }, [resultObject]);

  return (
    <div className="p-[20px] dkttb">
      <Spin
        spinning={loading}
        indicator={antIcon}
        tip={<span style={{ color: "var(--mk-p-color)" }}>Đang tải...</span>}
        size="large"
      >
        <Form layout="horizontal" form={form}>
          <Row gutter={[32, 12]}>
            <Col xs={24} sm={24} md={12} lg={12} xl={12}>
              <div className="p-[15px] md:p-[30px] bg-white rounded-3xl shadow-md">
                <div className="text-[18px] font-bold border-b-[3px] border-gray-300 pb-2 mb-4">
                  Thông tin đăng ký
                </div>
                <Form.Item
                  label="Loại giấy tờ GTTT"
                  name="id_type"
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  colon={false}
                  className="custom-form-item"
                >
                  <Select
                    bordered={false}
                    placeholder="Chọn loại GTTT"
                    labelInValue
                    onChange={(value) => {
                      form.setFieldsValue({ type: value.value });
                    }}
                    options={TYPEGTTT}
                  />
                </Form.Item>
                <Form.Item
                  label="Số GTTT"
                  name="id_number"
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  colon={false}
                  className="custom-form-item"
                >
                  <Input
                    placeholder="Nhập số giấy tờ"
                    id="id_number"
                    className="border-none shadow-none text-right"
                  />
                </Form.Item>
                <Form.Item
                  label="Nơi cấp GTTT"
                  name="id_issued_place"
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  colon={false}
                  className="custom-form-item"
                >
                  <Input
                    placeholder="Nơi cấp GTTT"
                    id="id_issued_place"
                    className="border-none shadow-none text-right"
                  />
                </Form.Item>
                <Form.Item
                  label="Chủ thuê bao"
                  name="name"
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  colon={false}
                  className="custom-form-item"
                >
                  <Input
                    placeholder="Nhập họ tên"
                    id="name"
                    className="border-none shadow-none text-right"
                  />
                </Form.Item>
                <Form.Item
                  label="Ngày sinh"
                  name="dob"
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  colon={false}
                  className="custom-form-item"
                >
                  <DatePicker
                    id="dob"
                    className="border-none shadow-none text-right"
                    placeholder="Chọn ngày sinh"
                    format={["DD/MM/YYYY", "DD-MM-YYYY", "DDMMYYYY"]}
                    disabledDate={(d) => !d || d.isAfter(Date.now())}
                  />
                </Form.Item>
                <Form.Item
                  label="Giới tính"
                  name="gender"
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  colon={false}
                  className="custom-form-item"
                >
                  <Select
                    bordered={false}
                    placeholder="Chọn giới tính"
                    labelInValue
                    onChange={(value) => {
                      form.setFieldsValue({ type: value.value });
                    }}
                    options={GENDER}
                  />
                </Form.Item>
                <Form.Item
                  label="Ngày hết hạn GTTT"
                  name="id_expired_date"
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  colon={false}
                  className="custom-form-item"
                >
                  {typeof form.getFieldValue("id_expired_date") === "string" ? (
                    <div
                      style={{
                        textAlign: "right",
                        width: "100%",
                        height: "32px",
                        lineHeight: "32px",
                      }}
                    >
                      {form.getFieldValue("id_expired_date")}
                    </div>
                  ) : (
                    <DatePicker
                      id="id_expired_date"
                      className="border-none shadow-none text-right"
                      placeholder="dd/mm/yyyy"
                      format={["DD/MM/YYYY", "DD-MM-YYYY", "DDMMYYYY"]}
                    />
                  )}
                </Form.Item>

                <Form.Item
                  label="Ngày cấp DTTT"
                  name="id_issued_date"
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  colon={false}
                  className="custom-form-item"
                >
                  <DatePicker
                    id="id_issued_date"
                    className="border-none shadow-none text-right"
                    placeholder="dd/mm/yyyy"
                    format={["DD/MM/YYYY", "DD-MM-YYYY", "DDMMYYYY"]}
                    disabledDate={(d) => !d || d.isAfter(Date.now())}
                  />
                </Form.Item>
              </div>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12} xl={12}>
              <div className="p-[15px] md:p-[30px] bg-white rounded-3xl shadow-md">
                <div className="text-[18px] font-bold border-b-[3px] border-gray-300 pb-2 mb-4">
                  Thông tin địa chỉ
                </div>
                <AddressModal
                  provinceTitle="Tỉnh/TP quê quán"
                  districtTitle="Quận/Huyện quê quán"
                  wardTitle="Xã/Phường quê quán"
                  provinceCode={provinceCode}
                  districtCode={districtCode}
                  wardCode={wardCode}
                  provinceField="province"
                  districtField="district"
                  wardField="ward"
                  setProvinceCode={setProvinceCode}
                  setDistrictCode={setDistrictCode}
                  setWardCode={setWardCode}
                />

                <AddressModal
                  provinceTitle="Tỉnh/TP thường trú"
                  districtTitle="Quận/Huyện thường trú"
                  wardTitle="Xã/Phường thường trú"
                  provinceCode={permanentProvinceCode}
                  districtCode={permanentDistrictCode}
                  wardCode={permanentWardCode}
                  provinceField="permanent_province"
                  districtField="permanent_district"
                  wardField="permanent_ward"
                  setProvinceCode={setPermanentProvinceCode}
                  setDistrictCode={setPermanentDistrictCode}
                  setWardCode={setPermanentWardCode}
                />

                <Form.Item
                  label="Nơi thường trú"
                  name="place_address"
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  colon={false}
                  className="custom-form-item"
                  hidden
                >
                  <Input
                    placeholder="Nhập địa chỉ"
                    id="name"
                    className="border-none shadow-none text-right"
                  />
                </Form.Item>
                <Form.Item
                  label="Địa chỉ thường trú"
                  name="permanent_address"
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  colon={false}
                  className="custom-form-item"
                  hidden
                >
                  <Input
                    placeholder="Nhập địa chỉ"
                    id="name"
                    className="border-none shadow-none text-right"
                  />
                </Form.Item>
                <Form.Item
                  label="Địa chỉ hiện tại"
                  name="contact_address"
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  colon={false}
                  className="custom-form-item"
                >
                  <Input
                    placeholder="Nhập địa chỉ"
                    id="name"
                    className="border-none shadow-none text-right"
                  />
                </Form.Item>

                <Form.Item
                  label="Ảnh phôi sim"
                  name="sim_base_image"
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  colon={false}
                  className="custom-form-item"
                >
                  <div className="item-row-container">
                    <div className="status-text">
                      {imgSimValue ? "Đã có" : "Chưa có"}
                    </div>
                    {imgSimValue && (
                      <div
                        className="fake-btn-link"
                        onClick={() => detail(imgSimValue, "sim_base_image")}
                      >
                        <span className="desktop-text">Xem chi tiết</span>
                        <span className="mobile-text">Xem</span>
                      </div>
                    )}
                  </div>
                </Form.Item>

                <Form.Item
                  label="Ảnh CCCD mặt trước"
                  name="id_front_image"
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  colon={false}
                  className="custom-form-item"
                >
                  <div className="item-row-container">
                    <div className="status-text">
                      {imgCccdFrontValue ? "Đã có" : "Chưa có"}
                    </div>
                    {imgCccdFrontValue && (
                      <div
                        className="fake-btn-link"
                        onClick={() =>
                          detail(imgCccdFrontValue, "id_front_image")
                        }
                      >
                        <span className="desktop-text">Xem chi tiết</span>
                        <span className="mobile-text">Xem</span>
                      </div>
                    )}
                  </div>
                </Form.Item>

                <Form.Item
                  label="Ảnh CCCD mặt sau"
                  name="id_back_image"
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  colon={false}
                  className="custom-form-item"
                >
                  <div className="item-row-container">
                    <div className="status-text">
                      {imgCccdBackValue ? "Đã có" : "Chưa có"}
                    </div>
                    {imgCccdBackValue && (
                      <div
                        className="fake-btn-link"
                        onClick={() =>
                          detail(imgCccdBackValue, "id_back_image")
                        }
                      >
                        <span className="desktop-text">Xem chi tiết</span>
                        <span className="mobile-text">Xem</span>
                      </div>
                    )}
                  </div>
                </Form.Item>

                <Form.Item
                  label="Ảnh chân dung"
                  name="portrait_image"
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  colon={false}
                  className="custom-form-item"
                >
                  <div className="item-row-container">
                    <div className="status-text">
                      {imgPortraitValue ? "Đã có" : "Chưa có"}
                    </div>
                    {imgPortraitValue && (
                      <div
                        className="fake-btn-link"
                        onClick={() =>
                          detail(imgPortraitValue, "portrait_image")
                        }
                      >
                        <span className="desktop-text">Xem chi tiết</span>
                        <span className="mobile-text">Xem</span>
                      </div>
                    )}
                  </div>
                </Form.Item>

                <Form.Item
                  label="Ảnh chữ ký"
                  name="signature_image"
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  colon={false}
                  className="custom-form-item"
                >
                  <div className="item-row-container">
                    <div className="status-text">
                      {imgSignatureValue ? "Đã có" : "Chưa có"}
                    </div>
                    {imgSignatureValue && (
                      <div
                        className="fake-btn-link"
                        onClick={() =>
                          detail(imgSignatureValue, "signature_image")
                        }
                      >
                        <span className="desktop-text">Xem chi tiết</span>
                        <span className="mobile-text">Xem</span>
                      </div>
                    )}
                  </div>
                </Form.Item>
              </div>
            </Col>
            <div className="flex justify-center">
              <div className="md:w-[80%] w-[90%]">
                <Button
                  type="primary"
                  block
                  className="md:mt-4 mt-0 p-[25px] text-[20px]"
                  onClick={handleConfirm}
                  loading={loading}
                >
                  Xác nhận
                </Button>
              </div>
            </div>
          </Row>
        </Form>
        {showModal && (
          <ModalItem
            title="Ký tên"
            show={showModal}
            onCloseModal={handleClose}
            className="modal-50"
          >
            <div className="text-center p-[10px]">
              <p className="text-[20px] font-bold mb-[10px]">
                Thông tin đăng ký
              </p>
              <div
                style={{
                  alignItems: "center",
                  flexDirection: "column",
                  display: "flex",
                  padding: "0px 0",
                }}
              >
                <img
                  src="/Group.png"
                  alt="avatar"
                  className="w-full h-[250px] object-none mt-4"
                />
                <p className="text-[16px] mt-8">
                  Xác nhận đơn hàng Đktttb. Đơn hàng của bạn đã được tiếp nhận
                </p>
              </div>
            </div>
          </ModalItem>
        )}
      </Spin>
    </div>
  );
};

export default DKTTBDetail;
